import { VoidCallback } from '@deqo/foundation';
import { Placement } from '@popperjs/core';
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { promptName } from '../../../CodesThatsGottaGoSomewhereElse';
import { Float, PromptProps, usePrompt } from '../../../ui';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { PopperInner, PopperMount, PopperPortal } from './PopperElements';

const MaybeUsePopperPortal = ({ children, portal }: { children: ReactNode, portal?: boolean }) =>
    portal ? <PopperPortal>{children}</PopperPortal> : <>{children}</>;

export const PopoverContext = React.createContext({
    layers: [] as string[],
});

export type PopoverButtonProps = {
    active: boolean,
    onClick: VoidCallback,
};

export type PopoverContentProps = {
    onClose: VoidCallback,
};

export type PopoverProps = {
    content: ReactNode,
    children: ReactNode,
    open: boolean,
    onOpenChange: (next: boolean) => void,
    placement?: Placement,
    inner?: { direction?: string, offset?: string },
    block?: boolean,
    portal?: boolean,
    disabled?: boolean,
    promptOpts?: PopoverPromptOpts,
    actionId?: string,
};

export type PopoverPromptOpts = Pick<PromptProps, 'ignoreEscape' | 'ignoreMouse' | 'clickOffIsPositive'>;

export const Popover = ({ children, content, open, onOpenChange, placement, inner, block, portal, disabled, promptOpts, actionId }: PopoverProps) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, { placement });

    const pn = useMemo(() => promptName(), []);
    const promptRef = usePrompt({
        name: pn,
        ignoreEnter: true,
        ...promptOpts,
        onAction: () => onOpenChange(false),
    });

    const onToggle = useCallback(() => onOpenChange(!open), [open, onOpenChange]);

    const { layers } = useContext(PopoverContext);
    const nextLayers = [...layers, pn];
    const ignore = nextLayers.map(x => `ignore-${x}`).join(' ');

    return <PopoverContext.Provider value={{ layers: nextLayers }}>
        <Flexer ref={promptRef} inline={!block}>
            <Flexer
                ref={setRefElmt}
                data-action={actionId}
                flex="row"
                onClick={disabled ? undefined : onToggle}
                pointerEvents={disabled ? 'none' : undefined}>
                {children}
            </Flexer>
            {open && !disabled &&
                <MaybeUsePopperPortal portal={portal}>
                    <PopperMount className={ignore} ref={setPopElmt} style={styles.popper} {...attributes.popper}>
                        <PopperInner {...(inner || {})}>
                            <Float shadow="xl">
                                {content}
                            </Float>
                        </PopperInner>
                    </PopperMount>
                </MaybeUsePopperPortal>
            }
        </Flexer>
    </PopoverContext.Provider>;
};


